/*
  The survey's rules, apart from rendering. The data is what GET /api/survey returns: sets and a
  general step, each a list of category sections holding questions. Answers are kept by survey
  question rid: a string for single select and free text, a list for multi select.

  A follow-up names its parent by source question rid. Within one step each source question
  appears once, so that is enough to find the parent. A follow-up shows only while its parent is
  showing and holds the trigger answer.
*/

const normalize = (value) => String(value ?? '').trim().toLowerCase();

export function isAnswered(question, value) {
  if (question.type === 'multi') return Array.isArray(value) && value.length > 0;
  return String(value ?? '').trim() !== '';
}

function matchesTrigger(value, expected) {
  const want = normalize(expected);
  if (Array.isArray(value)) return value.some((v) => normalize(v) === want);
  return normalize(value) === want;
}

// Every question in the step that is showing, by rid.
function visibleRids(sections, answers) {
  const bySource = new Map();
  for (const section of sections) {
    for (const question of section.questions) bySource.set(String(question.source_rid), question);
  }
  const memo = new Map();
  const visible = (question, seen = new Set()) => {
    if (memo.has(question.rid)) return memo.get(question.rid);
    let result = true;
    if (question.follow_up) {
      const parent = bySource.get(String(question.follow_up.parent_source_rid));
      // A parent outside this step, or a loop in the follow-up chain, can never be answered here.
      result = Boolean(parent)
        && !seen.has(parent.rid)
        && visible(parent, new Set([...seen, question.rid]))
        && matchesTrigger(answers[parent.rid], question.follow_up.answer);
    }
    memo.set(question.rid, result);
    return result;
  };
  const rids = new Set();
  for (const section of sections) {
    for (const question of section.questions) if (visible(question)) rids.add(question.rid);
  }
  return rids;
}

export function visibleSections(sections, answers) {
  const rids = visibleRids(sections, answers);
  return sections
    .map((section) => ({ ...section, questions: section.questions.filter((q) => rids.has(q.rid)) }))
    .filter((section) => section.questions.length > 0);
}

export function firstMissingAnswer(sections, answers) {
  for (const section of visibleSections(sections, answers)) {
    for (const question of section.questions) {
      if (question.required && !isAnswered(question, answers[question.rid])) return question;
    }
  }
  return null;
}

// What to send for one step: every visible question, blank if left empty. Hidden follow-ups are
// left out, and the server clears them.
export function buildAnswers(sections, answers) {
  return visibleSections(sections, answers).flatMap((section) =>
    section.questions.map((question) => {
      const value = answers[question.rid];
      if (question.type === 'multi') return { rid: question.rid, answer: Array.isArray(value) ? value : [] };
      return { rid: question.rid, answer: question.type === 'free_text' ? String(value ?? '').trim() : (value ?? '') };
    })
  );
}

export function buildSteps(survey) {
  const steps = (survey.sets || []).map((set) => ({ kind: 'set', set, sections: set.sections }));
  const general = survey.general?.sections || [];
  if (general.length > 0) steps.push({ kind: 'general', sections: general });
  return steps;
}

export function initialAnswers(survey) {
  const answers = {};
  for (const step of buildSteps(survey)) {
    for (const section of step.sections) {
      for (const question of section.questions) answers[question.rid] = question.answer;
    }
  }
  return answers;
}

export function toggleOption(list, option) {
  const current = Array.isArray(list) ? list : [];
  return current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
}

// Rich question text as one plain line, for an error message.
export function plainText(html) {
  return String(html ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h\d)>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
